"use client";

import * as React from "react";
import {
	PolarGrid,
	PolarRadiusAxis,
	RadialBar,
	RadialBarChart,
} from "recharts";

import {
	ChartContainer,
	ChartTooltip,
	ChartTooltipContent,
	seriesColor,
} from "./chart";
import {
	ChartCenterLabel,
	useTotal,
	type PolarChartProps,
} from "./chart-polar";

export type ChartRadialProps = PolarChartProps & {
	/**
	 * - `default` — concentric rings, one per category.
	 * - `grid` — adds a circular polar grid.
	 * - `text` — adds a total (or `centerLabel`) in the middle.
	 * - `shape` — rounded ring caps for a gauge-like treatment.
	 */
	variant?: "default" | "grid" | "text" | "shape";
	/** Start angle of the arc sweep. Defaults to `90`. */
	startAngle?: number;
	/** End angle of the arc sweep. Defaults to `-270` (a full circle). */
	endAngle?: number;
};

/**
 * Radial (ring / gauge) chart built on the shared `ChartContainer` primitives.
 * Palette-agnostic: each ring colour is read from `config` keyed by the ring's
 * category value, as `var(--color-<category>)`.
 */
export function ChartRadial({
	data,
	config,
	dataKey,
	nameKey,
	variant = "default",
	className,
	valueFormatter,
	hideTooltip = false,
	centerLabel,
	centerSublabel,
	startAngle = 90,
	endAngle = -270,
}: ChartRadialProps) {
	const withGrid = variant === "grid";
	const withCenterText = variant === "text";
	const cornerRadius = variant === "shape" ? 10 : 4;
	const total = useTotal(data, dataKey);

	const themedData = React.useMemo(
		() =>
			data.map((datum) => ({
				...datum,
				fill: seriesColor(String(datum[nameKey])),
			})),
		[data, nameKey],
	);

	return (
		<ChartContainer config={config} className={className}>
			<RadialBarChart
				data={themedData}
				startAngle={startAngle}
				endAngle={endAngle}
				innerRadius="30%"
				outerRadius="100%"
			>
				{!hideTooltip && (
					<ChartTooltip
						content={
							<ChartTooltipContent
								hideLabel
								valueFormatter={valueFormatter}
								nameKey={nameKey}
							/>
						}
					/>
				)}
				{withGrid && <PolarGrid gridType="circle" />}
				<RadialBar dataKey={dataKey} background cornerRadius={cornerRadius} />
				{withCenterText && (
					<PolarRadiusAxis tick={false} tickLine={false} axisLine={false}>
						<ChartCenterLabel
							label={centerLabel ?? total.toLocaleString()}
							sublabel={centerSublabel}
						/>
					</PolarRadiusAxis>
				)}
			</RadialBarChart>
		</ChartContainer>
	);
}
